import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gauge } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useRateLimitStatus } from '@/hooks/useRateLimitStatus';
import { cn } from '@/lib/utils';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';

interface RateLimitIndicatorProps {
  className?: string;
  collapsed?: boolean;
}

export function RateLimitIndicator({ className, collapsed = false }: RateLimitIndicatorProps) {
  const { status } = useRateLimitStatus();
  const [countdown, setCountdown] = useState(status.resetInSeconds);
  const hasNotifiedLowCredits = useRef(false);
  const navigate = useNavigate();

  // Update countdown timer
  useEffect(() => {
    if (status.exempt || status.remaining === 'unlimited') return;
    
    setCountdown(status.resetInSeconds);
    
    const interval = setInterval(() => {
      setCountdown(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [status.resetInSeconds, status.exempt, status.lastUpdated]);

  // One-time toast notification when credits drop to ≤5
  useEffect(() => {
    if (status.exempt || status.remaining === 'unlimited') return;
    
    const remaining = status.remaining as number;
    
    if (remaining <= 5 && !hasNotifiedLowCredits.current) {
      hasNotifiedLowCredits.current = true;
      toast({
        title: "Credits running low",
        description: `You have ${remaining} credit${remaining !== 1 ? 's' : ''} remaining.`,
        variant: "destructive",
        action: (
          <ToastAction altText="View usage" onClick={() => navigate('/credits')}>
            View Usage
          </ToastAction>
        ),
      });
    } else if (remaining > 5) {
      // Reset notification flag when credits go above threshold
      hasNotifiedLowCredits.current = false;
    }
  }, [status.remaining, status.exempt, navigate]);

  // Don't show if exempt or unlimited
  if (status.exempt || status.remaining === 'unlimited') {
    return null;
  }

  const remaining = status.remaining as number;
  const limit = status.limit as number;

  // Only show when credits are low (≤5 remaining)
  const shouldShow = remaining <= 5;

  // Determine status color based on remaining credits
  // Critical (0-2): red, Warning (3-5): amber
  let statusColor = 'text-amber-600 border-amber-200 bg-amber-50';
  const isCritical = remaining <= 2;
  if (isCritical) {
    statusColor = 'text-destructive border-destructive/20 bg-destructive/10';
  }

  const formatTime = (seconds: number) => {
    if (seconds >= 60) {
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="px-3 overflow-hidden"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.05 }}
            className="py-2"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/credits">
                  <Badge 
                    variant="outline" 
                    className={cn(
                      "gap-1 transition-all duration-300 overflow-hidden cursor-pointer hover:scale-105 hover:shadow-sm min-h-[22px] items-center", 
                      statusColor, 
                      collapsed && "px-1.5",
                      isCritical && "shadow-sm shadow-destructive/40",
                      className
                    )}
                  >
                    <Gauge className="w-3 h-3 flex-shrink-0" />
                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span
                          initial={{ opacity: 0, width: 0 }}
                          animate={{ opacity: 1, width: 'auto' }}
                          exit={{ opacity: 0, width: 0 }}
                          transition={{ duration: 0.2, delay: 0.25 }}
                          className="text-xs font-medium whitespace-nowrap"
                        >
                          {remaining}/{limit}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </Badge>
                </Link>
              </TooltipTrigger>
              <TooltipContent sideOffset={8}>
                <div className="text-sm">
                  <p className="font-medium">AI Credits Low</p>
                  <p className="text-muted-foreground">
                    {remaining} of {limit} credits remaining
                  </p>
                  <Link to="/credits" className="text-primary text-xs mt-1 font-medium block">
                    Get more credits →
                  </Link>
                </div>
              </TooltipContent>
            </Tooltip>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
